import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AuthContext from '../../context/authContext';
import useAirKey from '../../hooks/useAirKey';
import useBranch from '../../hooks/useBranch';
import useRole from '../../hooks/useRole';
import { toast } from 'react-toastify';
import { Navigate } from 'react-router-dom';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

export default function AirKeyBranchMappings() {
  const authCtx = useContext(AuthContext);
  const { fetchBranchMappings, upsertBranchMapping, deleteBranchMapping, fetchAreas } = useAirKey();
  const { getBranches } = useBranch();
  const { getRoles } = useRole();

  const [mappings, setMappings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const canUpdate = authCtx.checkRight('airkey:update');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, bRes, rRes, aRes] = await Promise.allSettled([
        fetchBranchMappings(),
        getBranches(),
        getRoles(),
        fetchAreas(),
      ]);
      setMappings(mRes.status === 'fulfilled' ? mRes.value?.mappings || [] : []);
      setBranches(bRes.status === 'fulfilled' ? bRes.value || [] : []);
      setRoles(
        rRes.status === 'fulfilled'
          ? Array.isArray(rRes.value)
            ? rRes.value
            : rRes.value?.roles || []
          : []
      );
      setAreas(aRes.status === 'fulfilled' ? aRes.value?.areas || [] : []);
    } catch (err) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [fetchBranchMappings, fetchAreas]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = useCallback(
    async formData => {
      setSaving(true);
      try {
        await upsertBranchMapping(formData);
        toast.success('Mapping gespeichert');
        setDialogOpen(false);
        setEditMapping(null);
        await loadData();
      } catch (err) {
        toast.error('Fehler beim Speichern');
      } finally {
        setSaving(false);
      }
    },
    [upsertBranchMapping, loadData]
  );

  const handleDelete = useCallback(
    async mappingId => {
      setDeletingId(mappingId);
      try {
        await deleteBranchMapping(mappingId);
        toast.success('Mapping gelöscht');
        await loadData();
      } catch (err) {
        toast.error('Fehler beim Löschen');
      } finally {
        setDeletingId(null);
      }
    },
    [deleteBranchMapping, loadData]
  );

  if (!authCtx.checkRight('airkey:read')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Paper sx={{ p: 2, ...viewRootTextPrimarySx }}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h6'>AirKey Branch-Mappings</Typography>
        {canUpdate && (
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => {
              setEditMapping(null);
              setDialogOpen(true);
            }}
          >
            Neues Mapping
          </Button>
        )}
      </Stack>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Definiert welche AirKey-Bereiche einem Standort + Rolle zugewiesen werden. Bei
        Mitarbeiter-Aktivierung werden alle passenden Mappings aggregiert.
      </Typography>

      {loading ? (
        <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Standort</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>AirKey-Bereiche</TableCell>
                {canUpdate && <TableCell sx={{ width: 100 }}>Aktionen</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map(m => (
                <TableRow key={m._id} hover>
                  <TableCell>
                    <Typography variant='body2' sx={!m.branchId ? { fontStyle: 'italic' } : undefined}>
                      {m.branchId
                        ? m.branchId?.longName || m.branchId?.locationName || m.branchName || '—'
                        : 'Alle Standorte'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      {m.roleId?.readableName || m.roleId?.name || m.roleName || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(m.airKeyAreaNames || []).map((name, i) => (
                        <Chip
                          key={m.airKeyAreaIds?.[i] || i}
                          label={name}
                          size='small'
                          variant='outlined'
                        />
                      ))}
                      {(!m.airKeyAreaNames || m.airKeyAreaNames.length === 0) &&
                        (m.airKeyAreaIds || []).map(id => (
                          <Chip key={id} label={`#${id}`} size='small' variant='outlined' />
                        ))}
                    </Box>
                  </TableCell>
                  {canUpdate && (
                    <TableCell>
                      <Stack direction='row' spacing={0.5}>
                        <Tooltip title='Bearbeiten'>
                          <IconButton
                            size='small'
                            onClick={() => {
                              setEditMapping(m);
                              setDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Löschen'>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleDelete(m._id)}
                            disabled={deletingId === m._id}
                          >
                            {deletingId === m._id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DeleteIcon fontSize='small' />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {mappings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canUpdate ? 4 : 3} align='center'>
                    <Typography color='text.secondary' sx={{ py: 4 }}>
                      Keine Mappings vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialogOpen && (
        <MappingDialog
          mapping={editMapping}
          branches={branches}
          roles={roles}
          areas={areas}
          saving={saving}
          onSave={handleSave}
          onCancel={() => {
            setDialogOpen(false);
            setEditMapping(null);
          }}
        />
      )}
    </Paper>
  );
}

function MappingDialog({ mapping, branches, roles, areas, saving, onSave, onCancel }) {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedAreas, setSelectedAreas] = useState([]);

  // Pre-fill when editing
  useEffect(() => {
    if (mapping) {
      const branchObj = mapping.branchId;
      const roleObj = mapping.roleId;
      if (branchObj && typeof branchObj === 'object') {
        setSelectedBranch({
          id: branchObj._id,
          label: branchObj.longName || branchObj.locationName || '',
        });
      }
      if (roleObj && typeof roleObj === 'object') {
        setSelectedRole({ id: roleObj._id, label: roleObj.readableName || roleObj.name || '' });
      }
      const areaList = (mapping.airKeyAreaIds || []).map((id, i) => ({
        areaId: id,
        areaName: mapping.airKeyAreaNames?.[i] || String(id),
      }));
      setSelectedAreas(areaList);
    }
  }, [mapping]);

  const branchOptions = branches.map(b => ({
    id: b._id,
    label: b.longName || b.locationName || b.name || b._id,
  }));

  const roleOptions = roles.map(r => ({
    id: r._id,
    label: r.readableName || r.name || r._id,
  }));

  const areaOptions = areas.map(a => ({
    areaId: a.areaId,
    areaName: a.areaName || String(a.areaId),
  }));

  const handleSubmit = () => {
    if (!selectedRole || selectedAreas.length === 0) return;
    const branch = selectedBranch
      ? branches.find(b => String(b._id) === String(selectedBranch.id))
      : null;
    const role = roles.find(r => String(r._id) === String(selectedRole.id));
    onSave({
      branchId: selectedBranch?.id || null,
      branchName: branch?.longName || branch?.locationName || '',
      roleId: selectedRole.id,
      roleName: role?.readableName || role?.name || '',
      airKeyAreaIds: selectedAreas.map(a => a.areaId),
      airKeyAreaNames: selectedAreas.map(a => a.areaName),
    });
  };

  return (
    <Dialog open onClose={onCancel} maxWidth='sm' fullWidth>
      <DialogTitle>{mapping ? 'Mapping bearbeiten' : 'Neues Mapping'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={branchOptions}
            value={selectedBranch}
            onChange={(_, val) => setSelectedBranch(val)}
            getOptionLabel={opt => opt.label || ''}
            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
            renderInput={params => (
              <TextField
                {...params}
                label='Standort'
                helperText='Leer lassen = gilt für alle Standorte'
              />
            )}
            disabled={!!mapping}
          />
          <Autocomplete
            options={roleOptions}
            value={selectedRole}
            onChange={(_, val) => setSelectedRole(val)}
            getOptionLabel={opt => opt.label || ''}
            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
            renderInput={params => <TextField {...params} label='Rolle' required />}
            disabled={!!mapping}
          />
          <Autocomplete
            multiple
            options={areaOptions}
            value={selectedAreas}
            onChange={(_, val) => setSelectedAreas(val)}
            getOptionLabel={opt => opt.areaName || String(opt.areaId)}
            isOptionEqualToValue={(opt, val) => opt.areaId === val.areaId}
            renderTags={(value, getTagProps) =>
              value.map((opt, index) => (
                <Chip
                  key={opt.areaId}
                  label={opt.areaName}
                  size='small'
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                label='AirKey-Bereiche'
                required
                helperText='Bereiche werden bei Sync aus AirKey geladen'
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!selectedRole || selectedAreas.length === 0 || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
